// Адрес раздела «Объекты». Правило 4: в адресе кодируются раздел, представление, фильтр,
// строка и открытая панель — ссылку коллега открывает в том же состоянии.
//
//   /sites/objects                       реестр объектов
//   /sites/objects?q=заря&f.active=true  поиск и фильтр «только действующие»
//   /sites/objects/r/<id>?tab=norm       карточка объекта, вкладка «Норматив»
//   /sites/customers/r/<id>              карточка заказчика
//   /sites/posts?f.site=<id>             посты выбранного объекта
//   ?p=new-site:new                      мастер «Новый объект» поверх раздела
//
// Чистые функции без React: их гоняют тесты и переиспользуют все три представления.

import { buildPath } from '@/shared/routing/paths';
import { PANEL_STAFF_POST } from '@/modules/people/route';
import { buildSearch, pushPanel, type SearchState } from '@/shared/routing/search';

export const SECTION = 'sites';

export const SITE_VIEWS = ['objects', 'customers', 'posts'] as const;
export type SitesView = (typeof SITE_VIEWS)[number];

/** Представление по умолчанию: реестр объектов. Оно же стоит в реестре разделов. */
export const DEFAULT_VIEW: SitesView = 'objects';

export function isSitesView(value: string | undefined): value is SitesView {
  return value !== undefined && (SITE_VIEWS as readonly string[]).includes(value);
}

/** Ключи фильтров раздела. Голая строка в разметке разъезжается с разбором адреса. */
export const FILTER_ACTIVE = 'active';
export const FILTER_CUSTOMER = 'customer';
export const FILTER_SITE = 'site';
export const FILTER_PERIOD = 'period';

export const CARD_TABS = ['overview', 'posts', 'norm', 'staffing', 'payroll'] as const;
export type CardTab = (typeof CARD_TABS)[number];

export const DEFAULT_TAB: CardTab = 'overview';

export function cardTab(value: string | undefined): CardTab {
  return value !== undefined && (CARD_TABS as readonly string[]).includes(value)
    ? (value as CardTab)
    : DEFAULT_TAB;
}

/** Виды панелей-мастеров раздела. */
export const PANEL_NEW_SITE = 'new-site';
export const PANEL_EDIT_SITE = 'site-edit';
export const PANEL_NEW_POST = 'new-post';
export const PANEL_EDIT_POST = 'post-edit';
/**
 * Правка заказчика и правка договора.
 *
 * Отдельными видами, а не одной панелью «правка справочника»: заголовок панели и состав полей
 * у них разные, а идентификатор в адресе — это опознаватель ИМЕННО той строки, которую правят.
 * Ссылка на правку договора, открытая коллегой, обязана открыть тот же договор.
 */
export const PANEL_EDIT_CUSTOMER = 'customer-edit';
export const PANEL_EDIT_CONTRACT = 'contract-edit';
/** Мастер «Новый заказчик» вместе с договором (тз, раздел 5.2; сценарий приёмки 11). */
export const PANEL_NEW_CUSTOMER = 'new-customer';
/**
 * Мастер укомплектования: поставить несколько человек на пост объекта.
 *
 * Имя объявлено в разделе «Люди» и берётся оттуда, а не пишется вторым текстом: панель одна
 * на два входа, и два одинаковых имени в разных файлах однажды разойдутся на одну букву.
 */
export { PANEL_STAFF_POST };
/** Идентификатор панели «Новый объект», когда заказчик заранее не выбран. */
export const NEW_SITE_BLANK = 'new';

export function sitesHref(view: SitesView, search: SearchState, rowId?: string): string {
  return `${buildPath({ section: SECTION, view, rowId })}${buildSearch(search)}`;
}

/** Разобранный адрес раздела: представление, выбранная строка и состояние поисковой строки. */
export interface SitesRoute {
  view: SitesView;
  rowId: string | undefined;
  search: SearchState;
}

/**
 * Мастер «Новый объект». Заведение объекта обратимо (объект закрывается, а не удаляется),
 * поэтому это панель, а не модалка: модалка остаётся за необратимым (правило 4).
 * Идентификатор панели — заказчик, если он уже выбран отбором: мастер откроется с ним.
 */
export function openNewSiteHref(route: SitesRoute): string {
  const customer = route.search.filters[FILTER_CUSTOMER];
  const next = pushPanel(route.search, {
    view: PANEL_NEW_SITE,
    id: customer ?? NEW_SITE_BLANK,
  });
  return sitesHref(route.view, next, route.rowId);
}

/** Мастер «Новый заказчик»: заказчик заводится обратимо, поэтому панель, а не модалка. */
export function openNewCustomerHref(route: SitesRoute): string {
  const next = pushPanel(route.search, { view: PANEL_NEW_CUSTOMER, id: NEW_SITE_BLANK });
  return sitesHref(route.view, next, route.rowId);
}

/** Правка объекта: город, регион, адрес, пояс, тип и договор. Закрытие — своей кнопкой. */
export function openEditSiteHref(route: SitesRoute, siteId: string): string {
  const next = pushPanel(route.search, { view: PANEL_EDIT_SITE, id: siteId });
  return sitesHref(route.view, next, route.rowId);
}

/** Правка заказчика: название, контакт, телефон, ИНН, отрасль, регион. */
export function openEditCustomerHref(route: SitesRoute, customerId: string): string {
  const next = pushPanel(route.search, { view: PANEL_EDIT_CUSTOMER, id: customerId });
  return sitesHref(route.view, next, route.rowId);
}

/** Правка договора: номер, даты, юрлицо-исполнитель. Заказчик договора не меняется. */
export function openEditContractHref(route: SitesRoute, contractId: string): string {
  const next = pushPanel(route.search, { view: PANEL_EDIT_CONTRACT, id: contractId });
  return sitesHref(route.view, next, route.rowId);
}

/**
 * Правка поста. В опознавателе панели ДВА идентификатора через косую черту: пост правится под
 * своим объектом, потому что видимость поста даёт объект, а не сам пост.
 */
export function openEditPostHref(route: SitesRoute, siteId: string, postId: string): string {
  const next = pushPanel(route.search, { view: PANEL_EDIT_POST, id: `${siteId}/${postId}` });
  return sitesHref(route.view, next, route.rowId);
}

/**
 * Разбор опознавателя панели правки поста обратно в пару «объект и пост».
 *
 * Возвращает `null` на всём, что не является ровно парой непустых кусков: панель тогда честно
 * говорит, чего в ссылке не хватает, вместо пустой формы над несуществующим постом.
 */
export function разобратьПост(id: string): { siteId: string; postId: string } | null {
  const [siteId, postId, ...лишнее] = id.split('/');
  if (siteId === undefined || postId === undefined || лишнее.length > 0) {
    return null;
  }
  return siteId === '' || postId === '' ? null : { siteId, postId };
}

/** Мастер «Новый пост» — тоже панель: пост заводится обратимо. */
export function openNewPostHref(route: SitesRoute, siteId: string): string {
  const next = pushPanel(route.search, { view: PANEL_NEW_POST, id: siteId });
  return sitesHref(route.view, next, route.rowId);
}

/** Мастер укомплектования объекта: панель, потому что назначение обратимо (закрывается датой). */
export function openStaffPostHref(route: SitesRoute, siteId: string): string {
  const next = pushPanel(route.search, { view: PANEL_STAFF_POST, id: siteId });
  return sitesHref(route.view, next, route.rowId);
}

/** Ставит или снимает один фильтр, не трогая остальные. */
export function setFilter(
  search: SearchState,
  key: string,
  value: string | undefined,
): SearchState {
  const filters: Record<string, string> = { ...search.filters };
  if (value === undefined || value === '') {
    delete filters[key];
  } else {
    filters[key] = value;
  }
  return { ...search, filters };
}

/**
 * Что переносится при переключении представления. Переносим ровно то, что в новом
 * представлении осмысленно: поиск по названию есть у объектов и заказчиков, а «посты»
 * без объекта — пустой экран, поэтому объект туда переносится обязательно.
 */
export function switchViewHref(
  from: SitesView,
  to: SitesView,
  search: SearchState,
  rowId: string | undefined,
): string {
  const filters: Record<string, string> = {};
  if (to === 'posts') {
    const site =
      from === 'objects' ? (rowId ?? search.filters[FILTER_SITE]) : search.filters[FILTER_SITE];
    if (site !== undefined) {
      filters[FILTER_SITE] = site;
    }
  }
  if (to === 'objects') {
    const customer =
      from === 'customers'
        ? (rowId ?? search.filters[FILTER_CUSTOMER])
        : search.filters[FILTER_CUSTOMER];
    if (customer !== undefined) {
      filters[FILTER_CUSTOMER] = customer;
    }
    const active = search.filters[FILTER_ACTIVE];
    if (active !== undefined) {
      filters[FILTER_ACTIVE] = active;
    }
  }
  const next: SearchState = {
    ...search,
    q: to === 'posts' ? undefined : search.q,
    // Вкладка карточки принадлежит открытой строке, а строка при смене представления уходит.
    tab: undefined,
    filters,
  };
  return sitesHref(to, next);
}
